<template>

  <div class="p-4">

    <RobloxOverviewStats />


    <TabButtons

      :buttons="panelSwitchingButtons"

      :active_button="currentlyVisiblePanel"

      :panel-changed-method="panelChanged"

    ></TabButtons>



    <!-- Generic loading for main panel (reports table) -->

    <div

      class="flex flex-col gap-2 justify-center items-center h-full"

      v-if="loading"

    >

      <h2>Fetching report data... This might take a few minutes.</h2>

      <LoadingComponent />

    </div>



    <div

      class="flex flex-col gap-2 justify-center items-center h-full"

      v-if="gender_loading && currentlyVisiblePanel === 'gender_table'"

    >

      <h2>Fetching gender data... This might take a few minutes.</h2>

      <LoadingComponent />

    </div>



    <!-- Gender Table -->

    <TableComponent

      v-if="currentlyVisiblePanel === 'gender_table' && !gender_loading"

      :table_data="genderTableComputed"

      default_sort_by="Game"

      :default_sort_desc="false"

      :exported_file_name="'gender-percentages-per-game'"

      :show_total_row="false"

      :show_average_row="false"

    ></TableComponent>



    <div v-if="currentlyVisiblePanel === 'age_table'">

      <div

        class="flex flex-col gap-2 justify-center items-center h-full"

        v-if="age_loading"

      >

        <h2>Fetching age data... This might take a few minutes.</h2>

        <LoadingComponent />

      </div>

      <!-- Age Table -->

      <h5 class="p-3 m-1">(Age table has had 'Unknown' column removed)</h5>

      <TableComponent

        v-if="!age_loading"

        :table_data="ageTableDataComputed"

        total_column_heading_name="Game"

        default_sort_by="Game"

        :default_sort_desc="false"

        :exported_file_name="'age-percentages-per-game'"

        :show_total_row="false"

        :show_average_row="false"

      ></TableComponent>

    </div>



    <!-- MAIN TABLE / REPORTS UI -->

    <div

      class="mt-4"

      v-if="(currentlyVisiblePanel === 'main_table' || currentlyVisiblePanel === 'us_table') && !loading"

    >

      <!-- Date Range Selector + Run Report -->

      <div class="flex flex-col gap-4 mb-4">

        <div class="flex items-center gap-4">

          <div class="flex items-center">

            <label class="mr-2 font-medium">Start Date:</label>

            <input

              type="date"

              v-model="startDate"

              class="p-2 border rounded-md mr-4"

            />

            <label class="mr-2 font-medium">End Date:</label>

            <input

              type="date"

              v-model="endDate"

              class="p-2 border rounded-md"

            />

          </div>

          <button

            type="button"

            @click="createReport"

            :disabled="creatingReport || !startDate || !endDate"

            class="py-1 px-4 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-60"

          >

            {{ creatingReport ? 'Submitting…' : 'Run Report' }}

          </button>

        </div>



        <!-- Buttons for Quick Ranges (sets dates only) -->

        <div class="flex gap-2">

          <button

            @click="setRange(7)"

            class="py-1 px-2 bg-blue-200 text-blue-800 hover:bg-blue-300 rounded"

          >

            Last 7 Days

          </button>

          <button

            @click="setRange(30)"

            class="py-1 px-2 bg-blue-200 text-blue-800 hover:bg-blue-300 rounded"

          >

            Last 30 Days

          </button>

          <button

            @click="setRange(60)"

            class="py-1 px-2 bg-blue-200 text-blue-800 hover:bg-blue-300 rounded"

          >

            Last 60 Days

          </button>

          <button

            @click="setRange(90)"

            class="py-1 px-2 bg-blue-200 text-blue-800 hover:bg-blue-300 rounded"

          >

            Last 90 Days

          </button>

        </div>

      </div>



      <!-- Reports List -->

      <div class="mb-6">

        <div class="flex gap-2 mb-3">

          <!-- All Games -->

          <button

            type="button"

            @click="showObbyOnly = false"

            :disabled="!showObbyOnly"

            class="px-3 py-1.5 text-sm rounded border

                  disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed

                  enabled:bg-white enabled:hover:bg-gray-100"

          >

            All Games

          </button>



          <!-- Obby Games -->

          <button

            type="button"

            @click="showObbyOnly = true"

            :disabled="showObbyOnly"

            class="px-3 py-1.5 text-sm rounded border

                  disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed

                  enabled:bg-white enabled:hover:bg-gray-100"

          >

            Obby Games

          </button>

        </div>



        <h3 class="text-lg font-semibold mb-2">Existing Reports</h3>



        <div v-if="reportsLoading" class="text-gray-600">

          Loading reports…

        </div>



        <div v-else-if="!reports.length" class="text-gray-500">

          No reports have been generated yet. Select a date range and run a report.

        </div>



        <div v-else class="space-y-1">

          <div

            v-for="report in reports"

            :key="report.id"

            class="flex items-center justify-between border rounded px-3 py-2 cursor-pointer hover:bg-gray-50"

            :class="{

              'border-blue-500 bg-blue-50': selectedReportId === report.id

            }"

            @click="handleReportClick(report)"

          >

            <div class="flex flex-col">

              <span class="font-medium">

                {{ formatDateMdy(report.start_date) }}

                →

                {{ formatDateMdy(report.end_date) }}

                ({{ getSpanDays(report) }} days)

              </span>

              <span class="text-xs text-gray-500">

                Status: {{ report.status }}

                <span v-if="report.entries_count != null">

                  • {{ report.entries_count }} rows

                </span>

                <span v-if="report.completed_at">

                  • Completed at: {{ formatUsDateTime(report.completed_at) }}

                </span>

              </span>

            </div>

            <span

              class="text-xs px-2 py-1 rounded"

              :class="statusBadgeClass(report.status)"

            >

              {{ report.status }}

            </span>

          </div>



          <!-- Pagination -->

          <div

            v-if="reportsPagination && reportsPagination.last_page > 1"

            class="flex items-center justify-between mt-2 text-sm text-gray-600"

          >

            <div>

              Page {{ reportsPagination.current_page }} of {{ reportsPagination.last_page }}

            </div>

            <div class="space-x-2">

              <button

                class="px-2 py-1 border rounded disabled:opacity-50"

                :disabled="reportsPagination.current_page <= 1"

                @click="changeReportsPage(reportsPagination.current_page - 1)"

              >

                Previous

              </button>

              <button

                class="px-2 py-1 border rounded disabled:opacity-50"

                :disabled="reportsPagination.current_page >= reportsPagination.last_page"

                @click="changeReportsPage(reportsPagination.current_page + 1)"

              >

                Next

              </button>

            </div>

          </div>

        </div>

      </div>



      <!-- Selected Report Table -->

      <div>

        <div class="flex items-center justify-between mb-2">

          <h2 class="text-xl font-semibold">Portfolio Data</h2>

          <span v-if="selectedReport" class="text-sm text-gray-600">

            Showing report:

            {{ formatDateMdy(selectedReport.start_date) }}

            →

            {{ formatDateMdy(selectedReport.end_date) }}

            ({{ getSpanDays(selectedReport) }} days, {{ selectedReport.status }})

          </span>

        </div>



        <TableComponent

          v-if="combinedPortfolioData.length > 0"

          :table_data="combinedPortfolioData"

          :default_sort_by="'Total Visits'"

          :default_sort_desc="true"

          :show_total_row="true"

          :show_average_row="true"

          :exported_file_name="selectedReportExportName"

          :total_column_heading_name="'Game'"

        />

        <p v-else class="text-gray-500">

          No data available for the selected report.

        </p>

      </div>

    </div>

  </div>

</template>



<script>

import TableComponent from "@/components/TableComponent.vue";
import RobloxOverviewStats from "@/components/RobloxOverviewStats.vue";

import TabButtons from "../components/TabButtons.vue";



export default {

  name: "PortfolioAnalytics",

  components: {

    TableComponent,
    RobloxOverviewStats,

    TabButtons,

  },

  data() {

    return {

      loading: false,

startDate: null,

      endDate: null,



      currentlyVisiblePanel: "main_table",



      // Reports / entries

      reports: [],

      reportsLoading: false,

      reportsPagination: null,

      reportEntries: [],

      selectedReportId: null,

      selectedReport: null,

      creatingReport: false,

      pollIntervalId: null,

      initialReportLoaded: false,



      showObbyOnly: false,



      // Gender / Age tables

      genderTableData: [],

      gender_loading: false,

      ageTableData: [],

      age_loading: false,



      panelSwitchingButtons: [

        { name: "main_table", text: "Main Table" },

        { name: "age_table", text: "Age %" },

        { name: "gender_table", text: "Gender %" },

      ],

    };

  },

  computed: {

    genderTableComputed() {

      let data = this.genderTableData;



      // Filter to obby-only if toggle is on

      if (this.showObbyOnly) {

        data = data.filter(row => row.is_obby === true);

      }



      // Remove is_obby column before returning to table

      // eslint-disable-next-line no-unused-vars

      return data.map((row) => {
        const result = { ...row };
        delete result.is_obby;
        return result;
      });

    },



    ageTableDataComputed() {

      let data = this.ageTableData;



      // Filter to obby-only if toggle is on

      if (this.showObbyOnly) {

        data = data.filter(row => row.is_obby === true);

      }



      // Remove is_obby column before returning to table

      // eslint-disable-next-line no-unused-vars

      return data.map((row) => {
        const result = { ...row };
        delete result.is_obby;
        return result;
      });

    },

    /**

     * Turns reportEntries from the API into the table shape:

     * Game, Total Visits, Avg. Daily Visits, Average DAU, Monthly Active Users,

     * Session Length (mins), US, AU, BR, CA, DE, ES, FR, GB, IT, MX, ROW.

     */

    computedReportEntries() {

      console.log('report entries', this.reportEntries);

      const entries = this.showObbyOnly

          ? this.reportEntries.filter((e) => e.is_obby === 1)

          : this.reportEntries;



      return entries;

    },

    combinedPortfolioData() {

      if (!Array.isArray(this.reportEntries) || this.reportEntries.length === 0) {

        return [];

      }



      const alpha2Codes = ["US", "AU", "BR", "CA", "DE", "ES", "FR", "GB", "IT", "MX"];



      return this.computedReportEntries.map((entry) => {

        const row = {

          Game: entry.friendly_name ?? null,

          "Total Visits": Number(entry.total_visits) || 0,

          "Avg. Daily Visits": Number(entry.avg_daily_visits) || 0,

          "Average DAU": Number(entry.average_dau) || 0,

          "Monthly Active Users": Number(entry.monthly_active_users) || 0,

          "Session Length (mins)": Number(entry.session_length_minutes) || 0,

          US: 0,

          AU: 0,

          BR: 0,

          CA: 0,

          DE: 0,

          ES: 0,

          FR: 0,

          GB: 0,

          IT: 0,

          MX: 0,

          ROW: 0,

        };



        const countries = Array.isArray(entry.countries) ? entry.countries : [];



        countries.forEach((c) => {

          if (!c || typeof c !== "object") return;

          const code = c.country_code;

          const val = Number(c.share_percent) || 0;

          if (!code) return;



          if (alpha2Codes.includes(code)) {

            row[code] += val;

            return;

          }



          // Anything unknown (including ROW) falls into ROW

          row.ROW += val;

        });



        alpha2Codes.forEach((code) => {

          row[code] = `${Math.round(row[code])}%`;

        });

        row.ROW = `${Math.round(row.ROW)}%`;



        return row;

      });

    },



    selectedReportExportName() {

      if (!this.selectedReport) return "portfolio-data";

      return `portfolio-data-${this.selectedReport.start_date}_to_${this.selectedReport.end_date}`;
    },

  },

  methods: {

    // --- UI helpers ---



    statusBadgeClass(status) {

      if (!status) return "bg-gray-200 text-gray-700";

      const s = String(status).toLowerCase();



      if (s === "completed") return "bg-green-100 text-green-800";

      if (s === "failed") return "bg-red-100 text-red-800";

      if (s.startsWith("running")) return "bg-yellow-100 text-yellow-800";

      if (s.startsWith("pending")) return "bg-blue-100 text-blue-800";



      return "bg-gray-200 text-gray-700";

    },



    formatDate(dateObj) {

      const year = dateObj.getFullYear();

      const month = String(dateObj.getMonth() + 1).padStart(2, "0");

      const day = String(dateObj.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;

    },



    formatDateMdy(isoDate) {

      if (!isoDate || typeof isoDate !== "string") return "";

      const parts = isoDate.split("-");

      if (parts.length !== 3) return isoDate;

      const [year, month, day] = parts;

      return `${Number(month)}/${Number(day)}/${year}`;

    },



    getSpanDays(report) {

      if (!report || !report.start_date || !report.end_date) return 0;

      const start = new Date(`${report.start_date}T00:00:00Z`);

      const end = new Date(`${report.end_date}T00:00:00Z`);

      const diffMs = end - start;

      if (!Number.isFinite(diffMs)) return 0;

      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;

      return days > 0 ? days : 0;

    },



    // --- Existing features ---



    fetchAgeTableData() {

      this.age_loading = true;

      this.$http

        .get(`/portfolio/age-table`)

        .then(({ data }) => {

          this.ageTableData = data;

          this.$notify({ type: "success", text: "Fetched age table data" });

        })

        .catch((error) => {

          this.$notify({ type: "error", text: "Failed to fetch age table data" });

          console.error(error.message);

        })

        .finally(() => {

          this.age_loading = false;

        });

    },



    fetchGenderTableData() {

      this.gender_loading = true;

      this.$http

        .get(`/portfolio/genders-table`)

        .then(({ data }) => {

          this.genderTableData = data;

          this.$notify({ type: "success", text: "Fetched gender table data" });

        })

        .catch((error) => {

          console.error(error.message);

          this.$notify({ type: "error", text: "Failed to fetch gender table data" });

        })

        .finally(() => {

          this.gender_loading = false;

        });

    },



    panelChanged(panelName) {

      this.currentlyVisiblePanel = panelName;

    },

    setRange(days) {

      const today = new Date();

      const oneDayAgo = new Date(today.getTime() - 24 * 60 * 60 * 1000);

      const rangeStart = new Date(

        oneDayAgo.getTime() - (days - 1) * 24 * 60 * 60 * 1000

      );

      this.startDate = this.formatDate(rangeStart);

      this.endDate = this.formatDate(oneDayAgo);

    },



    // --- Reports + entries ---



    async fetchReports(isPoll = false, page = null) {

      if (!isPoll) {

        this.reportsLoading = true;

      }



      try {

        let currentPage = 1;



        if (typeof page === "number" && page > 0) {

          currentPage = page;

        } else if (this.reportsPagination && this.reportsPagination.current_page) {

          currentPage = this.reportsPagination.current_page;

        }





        const { data } = await this.$http.get(`/game-kpi-reports`, {

          params: { per_page: 5, page: currentPage },

        });



        const reports = Array.isArray(data.data) ? data.data : [];

        this.reports = reports;

        this.reportsPagination = data.meta || {

          current_page: currentPage,

          last_page: 1,

          per_page: 5,

          total: reports.length,

        };



        // Keep selectedReport in sync with latest copy

        if (this.selectedReportId) {

          const match = reports.find((r) => r.id === this.selectedReportId);

          if (match) {

            this.selectedReport = match;

          }

        }



        // On first load only, pick the latest completed report

        if (!this.initialReportLoaded && reports.length) {

          const latestCompleted = this.findLatestCompletedReport(reports);

          if (latestCompleted) {

            this.selectedReportId = latestCompleted.id;

            this.selectedReport = latestCompleted;

            this.updateDateFromReport(latestCompleted);

            await this.loadReportEntries(latestCompleted);

          }

          this.initialReportLoaded = true;

        }

      } catch (error) {

        console.error("Failed to fetch reports", error);

        if (!isPoll) {

          this.$notify({

            type: "error",

            text: "Failed to fetch reports list",

          });

        }

      } finally {

        if (!isPoll) {

          this.reportsLoading = false;

        }

      }

    },



    formatUsDateTime(isoString) {

      if (!isoString) return "";



      const d = new Date(isoString);

      if (Number.isNaN(d.getTime())) return isoString; // fallback if it's not parseable



      const pad = (n) => String(n).padStart(2, "0");



      const month  = pad(d.getMonth() + 1); // 0-based

      const day    = pad(d.getDate());

      const year   = d.getFullYear();

      const hours  = pad(d.getHours());

      const minute = pad(d.getMinutes());



      return `${month}/${day}/${year} ${hours}:${minute}`;

    },



    changeReportsPage(page) {

      if (!this.reportsPagination) return;

      if (page < 1 || page > this.reportsPagination.last_page) return;

      this.fetchReports(false, page);

    },



    findLatestCompletedReport(reports) {

      const completed = reports.filter((r) => r.status === "completed");

      if (!completed.length) return null;



      return completed.reduce((latest, current) => {

        if (!latest) return current;

        const latestEnd = latest.end_date || "";

        const currentEnd = current.end_date || "";

        if (currentEnd > latestEnd) return current;

        if (currentEnd === latestEnd && current.id > latest.id) return current;

        return latest;

      }, null);

    },



    updateDateFromReport(report) {

      if (!report) return;

      this.startDate = report.start_date;

      this.endDate = report.end_date;

    },



    async loadReportEntries(report) {

      if (!report || !report.id) return;

      if (String(report.status).toLowerCase() !== "completed") {

        this.$notify({

          type: "error",

          text: "Report is not completed yet. Please wait until it finishes.",

        });

        return;

      }



      this.loading = true;



      try {

        const { data } = await this.$http.get(

          `/game-kpi-reports/${report.id}/entries`,

          { params: { per_page: 500 } }

        );



        this.reportEntries = Array.isArray(data.data) ? data.data : [];



        this.$notify({

          type: "success",

          text: "Loaded report table data",

        });

      } catch (error) {

        console.error("Failed to fetch report entries", error);

        this.$notify({

          type: "error",

          text: "Failed to load report entries",

        });

        this.reportEntries = [];

      } finally {

        this.loading = false;

      }

    },



    handleReportClick(report) {

      if (!report) return;



      this.selectedReportId = report.id;

      this.selectedReport = report;

      this.updateDateFromReport(report);



      if (String(report.status).toLowerCase() === "completed") {

        this.loadReportEntries(report);

        return;

      }



      this.$notify({

        type: "error",

        text: "Report is not completed yet. Status: " + report.status,

      });

    },



    async createReport() {

      if (!this.startDate || !this.endDate) {

        alert("Please select a start and end date.");

        return;

      }



      this.creatingReport = true;



      try {

        await this.$http.post(`/game-kpi-reports`, {

          startDate: this.startDate,

          endDate: this.endDate,

        });



        this.$notify({

          type: "success",

          text: "Report requested. It will appear in the list shortly.",

        });



        // Refresh page 1; polling will keep it updated after that.

        await this.fetchReports(false, 1);

      } catch (error) {

        console.error("Failed to create report", error);

        this.$notify({

          type: "error",

          text: "Failed to create report",

        });

      } finally {

        this.creatingReport = false;

      }

    },



    startPolling() {

      if (this.pollIntervalId) return;

      this.pollIntervalId = setInterval(() => {

        const page =

          (this.reportsPagination && this.reportsPagination.current_page) || 1;

        this.fetchReports(true, page);

      }, 10000);

    },



    stopPolling() {

      if (!this.pollIntervalId) return;

      clearInterval(this.pollIntervalId);

      this.pollIntervalId = null;

    },

  },

  mounted() {

    this.fetchGenderTableData();

    this.fetchAgeTableData();



    // Default the date range to the last 60 days in the picker

    const today = new Date();

    const oneDayAgo = new Date(today.getTime() - 24 * 60 * 60 * 1000);

    const sixtyDaysAgo = new Date(

      oneDayAgo.getTime() - 59 * 24 * 60 * 60 * 1000

    );

    this.startDate = this.formatDate(sixtyDaysAgo);

    this.endDate = this.formatDate(oneDayAgo);



    // Initial load of reports and their statuses

    this.fetchReports(false, 1);

    this.startPolling();

  },

  beforeUnmount() {

    this.stopPolling();

  },

};

</script>



<style scoped>

</style>